// src/App.tsx
import React, { useEffect } from "react";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import "/public/assets/css/particles.css"; 
import ContactForm from './components/ContactForm';

const App: React.FC = () => {
  // Load video optimizer script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/assets/js/video-optimizer.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  
  return (
    <>
      <Nav />
      <main className="w-full">
        {/* Floating light elements */}
        <div className="light x1"></div>
        <div className="light x2"></div>
        <div className="light x3"></div>
        <div className="light x4"></div>
        <div className="light x5"></div>
        <div className="light x6"></div>
        <div className="light x7"></div>
        <div className="light x8"></div>
        <div className="light x9"></div>
      
      {/* #### HERO SECTION #### */}
        <section id="hero" className="relative min-h-screen overflow-hidden">
          <div className="relative z-10 flex items-center min-h-screen">
            <div className="max-w-screen-xl mx-auto px-4 py-32 lg:py-48">
              <div className="max-w-3xl">
                <h1 className="mb-8 text-5xl md:text-7xl font-serif tracking-tight text-white">
                  Elevating Creator Partnerships
          </h1>
                <p className="mb-8 text-xl md:text-2xl font-light text-gray-300 max-w-2xl">
                  Echo Network bridges the gap between visionary brands and authentic creators, delivering campaigns that resonate, engage, and convert.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="#services"
                    className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-surface hover:bg-surface-hover transition-all duration-300 rounded-full"
                  >
                    Our Services
            <svg
              className="w-5 h-5 ml-2 -mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </a>
                </div>
        </div>
        </div>
      </div>
    </section>

        {/* #### STATS SECTION #### */}
        <section className="bg-[#0A0B14] py-20">
          <div className="max-w-screen-xl px-4 mx-auto text-center">
            <h2 className="mb-16 text-4xl md:text-5xl font-serif text-white">Our Impact</h2>
            <dl className="grid max-w-screen-md gap-8 mx-auto text-white sm:grid-cols-3">
              <div className="flex flex-col items-center justify-center p-6 border border-[#2D2F45] rounded-2xl hover:border-[#6927DA] transition-all duration-300 hover:bg-[#6927DA]/5 bg-[#1A1B23]">
                <dt className="mb-2 text-5xl md:text-6xl font-serif">100+</dt>
                <dd className="font-light text-gray-400 text-lg">Influencers collaborated</dd>
              </div>
              <div className="flex flex-col items-center justify-center p-6 border border-[#2D2F45] rounded-2xl hover:border-[#6927DA] transition-all duration-300 hover:bg-[#6927DA]/5 bg-[#1A1B23]">
                <dt className="mb-2 text-5xl md:text-6xl font-serif">40x</dt>
                <dd className="font-light text-gray-400 text-lg">Average ROAS</dd>
              </div>
              <div className="flex flex-col items-center justify-center p-6 border border-[#2D2F45] rounded-2xl hover:border-[#6927DA] transition-all duration-300 hover:bg-[#6927DA]/5 bg-[#1A1B23]">
                <dt className="mb-2 text-5xl md:text-6xl font-serif">45M+</dt>
                <dd className="font-light text-gray-400 text-lg">Engaged followers</dd>
              </div>
          </dl>
                    </div>
        </section>

        {/* #### APPROACH SECTION #### */}
        <section className="bg-[#0A0B14] py-32">
          <div className="max-w-screen-xl px-4 mx-auto">
            <h2 className="text-5xl md:text-6xl font-serif text-white text-center mb-20">
              Our blueprint for campaigns
                </h2>
            <div className="relative">
              {/* Connection line */}
              <div className="absolute hidden md:block w-full h-0.5 top-1/2 transform -translate-y-1/2">
                <div className="absolute w-[80%] h-[2px] left-[10%] bg-gradient-to-r from-[#6927DA] via-[#875BF7] to-[#6927DA]"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-16 relative z-10">
                {/* Creator Chemistry */}
                <div className="p-6 rounded-3xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA] group text-center">
                  <div className="h-14 w-14 bg-[#1A1B23] border-2 border-[#6927DA] rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-[#6927DA]/10">
                    <svg className="w-7 h-7 text-[#6927DA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-1">Creator</h3>
                  <h3 className="text-xl font-serif text-white mb-4">Chemistry 🔍</h3>
                  <p className="text-gray-400 text-sm">We pair your brand with perfect creator partners through real audience insights. Our match-making uncovers authentic connections that spark engagement and deliver measurable wins.</p>
                </div>

                {/* Creative Storytelling */}
                <div className="p-6 rounded-3xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA] group text-center">
                  <div className="h-14 w-14 bg-[#1A1B23] border-2 border-[#6927DA] rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-[#6927DA]/10">
                    <svg className="w-7 h-7 text-[#6927DA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-1">Creative</h3>
                  <h3 className="text-xl font-serif text-white mb-4">Storytelling 💡</h3>
                  <p className="text-gray-400 text-sm">Together, we transform your brand goals into compelling campaign concepts. Our collaborative process balances fresh ideas with strategic placement for maximum impact.</p>
                </div>

                {/* Execution Excellence */}
                <div className="p-6 rounded-3xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA] group text-center">
                  <div className="h-14 w-14 bg-[#1A1B23] border-2 border-[#6927DA] rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-[#6927DA]/10">
                    <svg className="w-7 h-7 text-[#6927DA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-1">Execution</h3>
                  <h3 className="text-xl font-serif text-white mb-4">Excellence 🚀</h3>
                  <p className="text-gray-400 text-sm">From concept approval to launch day, we handle every detail. We amplify your content through smart timing, cross-promotion, and engagement tactics that get results.</p>
                </div>

                {/* Results Revolution */}
                <div className="p-6 rounded-3xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA] group text-center">
                  <div className="h-14 w-14 bg-[#1A1B23] border-2 border-[#6927DA] rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-[#6927DA]/10">
                    <svg className="w-7 h-7 text-[#6927DA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  </div>
                  <h3 className="text-xl font-serif text-white mb-1">Results</h3>
                  <h3 className="text-xl font-serif text-white mb-4">Revolution 📊</h3>
                  <p className="text-gray-400 text-sm">We go beyond basic metrics to show the true impact of your creator partnerships. Clear insights fuel continuous improvement and set the stage for your next success story.</p>
                </div>
              </div>
                  </div>
                </div>
        </section>

        {/* #### SERVICES SECTION #### */}
        <section id="services" className="bg-[#0A0B14] py-20">
          <div className="max-w-screen-xl px-4 mx-auto text-center">
            <h2 className="mb-16 text-4xl md:text-5xl font-serif text-white">
              Services That Drive Results
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-8 rounded-2xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA]">
                <h3 className="text-2xl font-serif text-white mb-4">Influencer Strategy</h3>
                <p className="text-gray-400">We connect brands with perfectly aligned creators to deliver exceptional results that exceed KPIs.</p>
              </div>
              <div className="p-8 rounded-2xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA]">
                <h3 className="text-2xl font-serif text-white mb-4">Collaborative Marketing</h3>
                <p className="text-gray-400">Communication and transparency are our foundation, ensuring your message resonates with clarity and impact.</p>
              </div>
              <div className="p-8 rounded-2xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA]">
                <h3 className="text-2xl font-serif text-white mb-4">Social Media Strategy</h3>
                <p className="text-gray-400">Our expertise helps develop and implement specialized strategies that grow both creators and brands.</p>
              </div>
              <div className="p-8 rounded-2xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA]">
                <h3 className="text-2xl font-serif text-white mb-4">Content Production</h3>
                <p className="text-gray-400">From ideation to full production, our experienced team delivers versatile content that captivates audiences.</p>
                  </div>
              <div className="p-8 rounded-2xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA]">
                <h3 className="text-2xl font-serif text-white mb-4">Talent Management</h3>
                <p className="text-gray-400">We help content creators transform their influence into sustainable careers while connecting brands to authentic voices.</p>
              </div>
              <div className="p-8 rounded-2xl bg-[#1A1B23] border border-[#2D2F45] transition-all duration-300 hover:border-[#6927DA]">
                <h3 className="text-2xl font-serif text-white mb-4">Creative Campaigns</h3>
                <p className="text-gray-400">Our innovative campaigns reinforce your brand strategy to drive reach, engagement, and measurable conversions.</p>
                </div>
              </div>
            </div>
          </section>

        {/* #### STATS HIGHLIGHT SECTION #### */}
        <section className="bg-[#0A0B14] py-20 relative overflow-hidden">
          {/* Video Background - Adjusted to fit inside section better */}
          <div className="absolute inset-0 z-0 bg-[#0A0B14]/70 flex items-center justify-center">
            <div className="relative w-[85%] h-[95%] rounded-3xl overflow-hidden">
              <video 
                className="absolute object-cover w-full h-full opacity-50" 
                autoPlay 
                muted 
                loop 
                playsInline
                src="/assets/videos/about_video-1.mp4"
                aria-hidden="true"
              >
                <source src="/assets/videos/about_video-1.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
          
          {/* Content with higher z-index */}
          <div className="max-w-screen-xl px-4 mx-auto text-center relative z-10">
            <h2 className="mb-16 text-4xl md:text-5xl font-serif text-white">Creator Marketing Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-8 max-w-[320px] mx-auto w-full rounded-2xl bg-[#1A1B23]/80 backdrop-blur-sm border border-[#2D2F45] hover:border-[#6927DA] transition-all duration-300">
                <p className="text-4xl font-serif text-white mb-4">2.8x</p>
                <p className="text-gray-400">higher conversion rates compared to traditional paid media</p>
              </div>
              <div className="p-8 max-w-[320px] mx-auto w-full rounded-2xl bg-[#1A1B23]/80 backdrop-blur-sm border border-[#2D2F45] hover:border-[#6927DA] transition-all duration-300">
                <p className="text-4xl font-serif text-white mb-4">62%</p>
                <p className="text-gray-400">of consumers trust creator recommendations over traditional advertising</p>
              </div>
              <div className="p-8 max-w-[320px] mx-auto w-full rounded-2xl bg-[#1A1B23]/80 backdrop-blur-sm border border-[#2D2F45] hover:border-[#6927DA] transition-all duration-300">
                <p className="text-4xl font-serif text-white mb-4">32%</p>
                <p className="text-gray-400">decrease in customer acquisition costs through targeted creator partnerships</p>
              </div>
            </div>
          </div>
        </section>

        {/* #### ABOUT SECTION #### */}
        <section id="about" className="bg-background py-20">
          <div className="gap-16 items-center py-8 px-4 mx-auto max-w-screen-xl lg:grid lg:grid-cols-2 lg:py-16 lg:px-6">
            <div className="text-gray-400">
              <h2 className="mb-4 text-4xl md:text-5xl font-serif text-white">
                About Echo Network
              </h2>
              <p className="mb-4 text-2xl text-gray-300">
                We're a nimble, results-driven agency founded on the belief that authentic creator partnerships drive exceptional business outcomes.
              </p>
              <p className="text-xl">
                Our team combines data-driven strategy with creative storytelling to deliver campaigns that not only reach audiences but inspire action. Though we're new, our approach is proven—we've already helped brands achieve an average 40x return on ad spend through strategic creator collaborations.
              </p>
              <a 
                href="#contact" 
                className="inline-flex mt-8 items-center justify-center px-8 py-4 text-white bg-surface hover:bg-surface-hover transition-all duration-300 rounded-full text-base font-medium"
          >
                Get Started
          </a>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <img
                className="w-full rounded-lg transition-all duration-300 hover:opacity-75 h-[385px] object-cover object-center"
                src="./assets/images/echonw_4.jpg"
                alt="Echo Network team"
              />
              <img
                className="mt-4 w-full rounded-lg lg:mt-10 transition-all duration-300 hover:opacity-75 h-[385px] object-cover object-center"
                src="./assets/images/echonw_1.jpg"
                alt="Echo Network workspace"
              />
            </div>
          </div>
        </section>

        {/* #### CONTACT SECTION #### */}
        <section id="contact" className="bg-[#0A0B14] py-20">
          <div className="max-w-screen-xl px-4 mx-auto">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="mb-8 text-4xl md:text-5xl font-serif text-white">
                Ready to Amplify Your Brand?
              </h2>
              <p className="mb-12 text-gray-400 text-xl">
                Let's discuss how Echo Network can elevate your brand through strategic creator partnerships that deliver measurable results.
              </p>
              <ContactForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default App;
